import { useAdminInvoiceReportViewers } from '@/context/services/admin_invoice_reports/AdminInvoiceReportViewers.service';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect } from 'react';

type UseShareViewersProps = {
  reportId: string;
  refetch: () => void | Promise<unknown>;
  onCloseDetailDrawer: () => void;
};

/**
 * Same error handling as the non-admin `ShareDrawer/hooks/useShareViewers`:
 * 401/403 goes through a permission recheck, anything else shows a toast
 * and closes the detail drawer along with it. `refetch` is passed in
 * explicitly rather than pulled from context, matching
 * `useCancelAdminInvoiceReport`'s pattern for the admin list.
 */
export const useShareViewers = ({
  reportId,
  refetch,
  onCloseDetailDrawer,
}: UseShareViewersProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const { data, error } = useAdminInvoiceReportViewers(reportId);

  const showErrorMessages = useCallback(
    (messages?: string | string[]) => {
      const title = Array.isArray(messages)
        ? messages.join(',')
        : messages || t('system_error');
      notify.error({ title, duration: 3 });
    },
    [notify, t]
  );

  const onCheckAccessSuccess = useCallback(() => {
    onCloseDetailDrawer();
    showErrorMessages(error?.messages);
  }, [onCloseDetailDrawer, error?.messages, showErrorMessages]);

  useEffect(() => {
    if (!error) return;
    if (error.code === '401' || error.code === '403') {
      handleCheckPermission({
        fnCheckPermission: refetch,
        onCheckSuccess: onCheckAccessSuccess,
        onCheckFailure: onCloseDetailDrawer,
      });
    } else {
      showErrorMessages(error.messages);
      onCloseDetailDrawer();
    }
  }, [
    error,
    onCloseDetailDrawer,
    refetch,
    handleCheckPermission,
    showErrorMessages,
    onCheckAccessSuccess,
  ]);

  return { data };
};
